// src/presentation/components/EditPatientDialog.tsx

import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, Loader2 } from "lucide-react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { toDateOnlyISO } from "@/lib/dateUtils";
import { textStyles } from "@/lib/textStyles";

import { useUpdatePatientMutation } from "../../infrastructure/api/patientsApi";

const NAME_MAX_LENGTH = 50;
const FULL_NAME_MAX_LENGTH = NAME_MAX_LENGTH * 2 + 1; // "First Last"
const FULL_NAME_DISALLOWED_CHARS = /[^A-Za-z0-9. ]/g;

const sanitizeFullNameInput = (value: string) =>
  value.replace(FULL_NAME_DISALLOWED_CHARS, "").slice(0, FULL_NAME_MAX_LENGTH);

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  patient: any;
  onSuccess?: () => void;
}

interface FormData {
  mobileNumber: string;
  name: string;
  email: string;
  gender: string;
  ethnicity: string;
  dateOfBirth?: Date;
  caregiverName: string;
  caregiverMobile: string;
}

export const EditPatientDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  patient,
  onSuccess,
}) => {
  const { t } = useTranslation("patients");
  const genderOptions = [
    { value: "Male", label: t("patientForm.genderOptions.male") },
    { value: "Female", label: t("patientForm.genderOptions.female") },
    { value: "Other", label: t("patientForm.genderOptions.other") },
  ];
  const ethnicityOptions = [
    {
      value: "American Indian or Alaska Native",
      label: t("patientForm.ethnicityOptions.americanIndian"),
    },
    { value: "Black or African", label: t("patientForm.ethnicityOptions.black") },
    { value: "East Asian", label: t("patientForm.ethnicityOptions.eastAsian") },
    { value: "South Asian", label: t("patientForm.ethnicityOptions.southAsian") },
    {
      value: "Southeast Asian",
      label: t("patientForm.ethnicityOptions.southeastAsian"),
    },
    {
      value: "Middle Eastern or North African",
      label: t("patientForm.ethnicityOptions.middleEastern"),
    },
    {
      value: "Native Hawaiian or Pacific Islander",
      label: t("patientForm.ethnicityOptions.nativeHawaiian"),
    },
    { value: "White or European", label: t("patientForm.ethnicityOptions.white") },
    { value: "Latino or Hispanic", label: t("patientForm.ethnicityOptions.latino") },
    { value: "Mixed or Multiracial", label: t("patientForm.ethnicityOptions.mixed") },
    { value: "Other", label: t("patientForm.ethnicityOptions.other") },
    {
      value: "Prefer not to say",
      label: t("patientForm.ethnicityOptions.preferNotToSay"),
    },
  ];
  const [formData, setFormData] = useState<FormData>({
    mobileNumber: "",
    name: "",
    email: "",
    gender: "",
    ethnicity: "",
    dateOfBirth: undefined,
    caregiverName: "",
    caregiverMobile: "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>(
    {},
  );
  const [dobCalendarOpen, setDobCalendarOpen] = useState(false);

  const [updatePatient, { isLoading }] = useUpdatePatientMutation();

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (patient) {
      setFormData({
        mobileNumber: patient.phone || "",
        name: patient.name || "",
        email: patient.email || "",
        gender: patient.gender || "",
        ethnicity: patient.ethnicity || "",
        dateOfBirth: patient.dateOfBirth
          ? new Date(patient.dateOfBirth)
          : undefined,
        caregiverName: patient.caregiverName || "",
        caregiverMobile: patient.caregiverNumber || "",
      });
      setErrors({});
    }
  }, [patient]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const isFormValid =
    formData.name.trim() &&
    formData.mobileNumber.trim() &&
    formData.gender &&
    formData.ethnicity &&
    formData.dateOfBirth &&
    !errors.email &&
    !errors.mobileNumber &&
    !errors.caregiverMobile;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = (field: keyof FormData, value: any) => {
    const nextValue = field === "name" ? sanitizeFullNameInput(value) : value;
    setFormData((prev) => ({ ...prev, [field]: nextValue }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleEmailChange = (value: string) => {
    setFormData((prev) => ({ ...prev, email: value }));
    if (value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
      setErrors((prev) => ({
        ...prev,
        email: t("patientForm.validation.emailInvalid"),
      }));
    } else {
      setErrors((prev) => ({ ...prev, email: undefined }));
    }
  };

  const handlePhoneChange = (
    field: "mobileNumber" | "caregiverMobile",
    val: string,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: val }));
    if (val && !isValidPhoneNumber(val)) {
      setErrors((prev) => ({
        ...prev,
        [field]: t("patientForm.validation.mobileInvalid"),
      }));
    } else {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleDobChange = (date: Date | undefined) => {
    handleChange("dateOfBirth", date);
    setDobCalendarOpen(false);
  };

  const handleClose = () => {
    setErrors({});
    setDobCalendarOpen(false);
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    try {
      const [first_name, ...rest] = formData.name.split(" ");
      const last_name = rest.join(" ");

      await updatePatient({
        patientId: patient.id,
        data: {
          first_name,
          last_name,
          phone: formData.mobileNumber,
          email: formData.email,
          gender: formData.gender,
          ethnicity: formData.ethnicity,
          date_of_birth: formData.dateOfBirth
            ? toDateOnlyISO(formData.dateOfBirth)
            : undefined,
          caregiver_name: formData.caregiverName,
          caregiver_number: formData.caregiverMobile,
        },
      }).unwrap();

      toast.success(t("editPatientDialog.success"));
      onSuccess?.();
      onOpenChange(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      toast.error(err?.data?.message || t("editPatientDialog.error"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="flex h-[calc(100dvh-1rem)] flex-col overflow-hidden rounded-xl sm:h-[700px] sm:max-w-[650px]"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {t("editPatientDialog.title")}
          </DialogTitle>
          <p className="text-sm text-gray-500 mt-1">
            {t("editPatientDialog.subtitle")}
          </p>
        </DialogHeader>

        <div
          className="min-h-0 flex-1 space-y-6 overflow-y-auto py-4 [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: "none" }}
        >
          {/* ── Patient Information ───────────────────────────── */}
          <div className="space-y-4">
            <h3 className="text-base font-medium text-gray-900 border-b pb-2">
              {t("addPatientDialog.patientInfoTitle")}
            </h3>

            <div className="space-y-4">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium">
                  {t("editPatientDialog.nameLabel")}{" "}
                  <span className="text-destructive-text">*</span>
                </Label>
                <Input
                  id="name"
                  placeholder={t("editPatientDialog.namePlaceholder")}
                  value={formData.name}
                  maxLength={FULL_NAME_MAX_LENGTH}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className={cn(
                    "w-full",
                    errors.name ? "border-destructive" : "",
                  )}
                />
                {errors.name && (
                  <p className="text-xs text-destructive-text">{errors.name}</p>
                )}
              </div>

              {/* Mobile Number */}
              <div className="space-y-2">
                <Label htmlFor="mobileNumber" className="text-sm font-medium">
                  {t("patientForm.mobileLabel")}{" "}
                  <span className="text-destructive-text">*</span>
                </Label>
                <div
                  className={cn(
                    "flex h-10 rounded-md border overflow-hidden",
                    errors.mobileNumber ? "border-destructive" : "border-input",
                  )}
                >
                  <PhoneInput
                    id="mobileNumber"
                    defaultCountry="IN"
                    international
                    countryCallingCodeEditable={false}
                    placeholder={t("patientForm.mobilePlaceholder")}
                    value={formData.mobileNumber}
                    onChange={(val) =>
                      handlePhoneChange("mobileNumber", val ?? "")
                    }
                  />
                </div>
                {errors.mobileNumber && (
                  <p className="text-xs text-destructive-text">
                    {errors.mobileNumber}
                  </p>
                )}
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  {t("patientForm.emailLabel")}
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t("patientForm.emailPlaceholder")}
                  value={formData.email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  className={cn(
                    "w-full",
                    errors.email ? "border-destructive" : "",
                  )}
                />
                {errors.email && (
                  <p className="text-xs text-destructive-text">
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Gender */}
              <div className="space-y-2">
                <Label htmlFor="gender" className="text-sm font-medium">
                  {t("patientForm.genderLabel")}{" "}
                  <span className="text-destructive-text">*</span>
                </Label>
                <Select
                  value={formData.gender}
                  onValueChange={(v) => handleChange("gender", v)}
                >
                  <SelectTrigger
                    id="gender"
                    className={cn(
                      "w-full",
                      errors.gender ? "border-destructive" : "",
                    )}
                  >
                    <SelectValue placeholder={t("patientForm.selectGender")} />
                  </SelectTrigger>
                  <SelectContent
                    position="popper"
                    side="bottom"
                    align="start"
                    sideOffset={4}
                    className="z-50"
                  >
                    {genderOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.gender && (
                  <p className="text-xs text-destructive-text">
                    {errors.gender}
                  </p>
                )}
              </div>

              {/* Ethnicity */}
              <div className="space-y-2">
                <Label htmlFor="ethnicity" className="text-sm font-medium">
                  {t("patientForm.ethnicityLabel")}{" "}
                  <span className="text-destructive-text">*</span>
                </Label>
                <Select
                  value={formData.ethnicity}
                  onValueChange={(v) => handleChange("ethnicity", v)}
                >
                  <SelectTrigger
                    id="ethnicity"
                    className={cn(
                      "w-full",
                      errors.ethnicity ? "border-destructive" : "",
                    )}
                  >
                    <SelectValue placeholder={t("patientForm.selectEthnicity")} />
                  </SelectTrigger>
                  <SelectContent
                    position="popper"
                    side="bottom"
                    align="start"
                    sideOffset={4}
                    className="z-50"
                  >
                    {ethnicityOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.ethnicity && (
                  <p className="text-xs text-destructive-text">
                    {errors.ethnicity}
                  </p>
                )}
              </div>

              {/* Date of Birth */}
              <div className="space-y-2">
                <Label htmlFor="dateOfBirth" className="text-sm font-medium">
                  {t("patientForm.dobLabel")}{" "}
                  <span className="text-destructive-text">*</span>
                </Label>
                <Popover
                  open={dobCalendarOpen}
                  onOpenChange={setDobCalendarOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.dateOfBirth && "text-muted-foreground",
                        errors.dateOfBirth ? "border-destructive" : "",
                      )}
                      style={textStyles.body}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.dateOfBirth ? (
                        format(formData.dateOfBirth, "dd/MM/yyyy")
                      ) : (
                        <span>{t("patientForm.dobPlaceholder")}</span>
                      )}
                    </Button>
                  </PopoverTrigger>

                  <PopoverContent className="w-auto p-3" align="start">
                    <div className="flex gap-2 mb-3">
                      <Select
                        value={
                          formData.dateOfBirth
                            ? String(formData.dateOfBirth.getMonth())
                            : undefined
                        }
                        onValueChange={(month) => {
                          const newDate = formData.dateOfBirth || new Date();
                          newDate.setMonth(Number(month));
                          setFormData((prev) => ({
                            ...prev,
                            dateOfBirth: new Date(newDate),
                          }));
                        }}
                      >
                        <SelectTrigger className="w-full sm:w-[120px]">
                          <SelectValue placeholder={t("patientForm.monthPlaceholder")} />
                        </SelectTrigger>
                        <SelectContent
                          className="w-[100px] max-h-60 overflow-y-auto"
                          position="popper"
                          side="bottom"
                          align="start"
                        >
                          {Array.from({ length: 12 }).map((_, i) => (
                            <SelectItem key={i} value={String(i)}>
                              {format(new Date(0, i), "MMM")}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Select
                        value={
                          formData.dateOfBirth
                            ? String(formData.dateOfBirth.getFullYear())
                            : undefined
                        }
                        onValueChange={(year) => {
                          const newDate = formData.dateOfBirth || new Date();
                          newDate.setFullYear(Number(year));
                          setFormData((prev) => ({
                            ...prev,
                            dateOfBirth: new Date(newDate),
                          }));
                        }}
                      >
                        <SelectTrigger className="w-full sm:w-[100px]">
                          <SelectValue placeholder={t("patientForm.yearPlaceholder")} />
                        </SelectTrigger>
                        <SelectContent
                          className="w-[100px] max-h-60 overflow-y-auto"
                          position="popper"
                          side="bottom"
                          align="start"
                        >
                          {Array.from({ length: 100 }).map((_, i) => {
                            const year = new Date().getFullYear() - i;
                            return (
                              <SelectItem key={year} value={String(year)}>
                                {year}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>

                    <Calendar
                      mode="single"
                      selected={formData.dateOfBirth}
                      onSelect={handleDobChange}
                      month={formData.dateOfBirth || new Date()}
                      onMonthChange={(month) => {
                        setFormData((prev) => ({
                          ...prev,
                          dateOfBirth: new Date(month),
                        }));
                      }}
                      disabled={(date) =>
                        date > new Date() || date < new Date("1900-01-01")
                      }
                      initialFocus
                      classNames={{
                        day_selected: "!bg-primary !text-white rounded-md",
                      }}
                    />
                  </PopoverContent>
                </Popover>
                {errors.dateOfBirth && (
                  <p className="text-xs text-destructive-text">
                    {errors.dateOfBirth}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* ── Emergency Contact ─────────────────────────────── */}
          <div className="space-y-4">
            <h3 className="text-base font-medium text-gray-900 border-b pb-2">
              {t("patientForm.emergencyContactTitle")}
            </h3>

            <div className="space-y-4">
              {/* Caregiver Name */}
              <div className="space-y-2">
                <Label htmlFor="caregiverName" className="text-sm font-medium">
                  {t("patientForm.emergencyContactNameLabel")}
                </Label>
                <Input
                  id="caregiverName"
                  placeholder={t("patientForm.emergencyContactNameLabel")}
                  value={formData.caregiverName}
                  onChange={(e) => handleChange("caregiverName", e.target.value)}
                  className="w-full"
                />
              </div>

              {/* Caregiver Mobile */}
              <div className="space-y-2">
                <Label
                  htmlFor="caregiverMobile"
                  className="text-sm font-medium"
                >
                  {t("patientForm.emergencyContactMobileLabel")}
                </Label>
                <div
                  className={cn(
                    "flex h-10 rounded-md border overflow-hidden",
                    errors.caregiverMobile
                      ? "border-destructive"
                      : "border-input",
                  )}
                >
                  <PhoneInput
                    id="caregiverMobile"
                    defaultCountry="IN"
                    international
                    countryCallingCodeEditable={false}
                    placeholder={t("patientForm.mobilePlaceholder")}
                    value={formData.caregiverMobile}
                    onChange={(val) =>
                      handlePhoneChange("caregiverMobile", val ?? "")
                    }
                  />
                </div>
                {errors.caregiverMobile && (
                  <p className="text-xs text-destructive-text">
                    {errors.caregiverMobile}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer ───────────────────────────────────────────── */}
        <div className="flex shrink-0 items-center justify-end gap-3 border-t pt-4">
          <Button variant="outline" onClick={handleClose} className="px-6">
            {t("patientForm.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid}
            loading={isLoading}
            className="px-6 bg-blue-600 hover:bg-blue-600 cursor-pointer text-white"
          >
            {t("editPatientDialog.save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
